<template>
  <card id="synthesis" :title="$t('f-synthesis:synthesis')" no-body no-footer>
    <ul v-if="totalCount>0" slot="card-table" class="list-group list-group-flush">
      <li class="list-group-item d-flex justify-content-between align-items-center border-0">
        <a id="goToConnectionFilterLink" href="/#/gateways" @click="setGatewayFilters({ states: { connection: ['FALSE'] } })">{{ $t('f-synthesis:disconnected') | capitalize }}</a>
        <span>{{ disconnectedCount }}</span>
      </li>

      <li class="list-group-item d-flex justify-content-between align-items-center border-0">
        <a id="gotToDoorFilterLink" href="/#/gateways" @click="setGatewayFilters({ states: { door: ['OPEN'] } })">{{ $t('f-synthesis:openDoors') | capitalize }}</a>
        <span>{{ openCount }}</span>
      </li>

      <li class="list-group-item d-flex justify-content-between align-items-center border-0">
        <a id="goToGpsFilterLink" href="/#/gateways" @click="setGatewayFilters({ states: { gps: ['UNLOCKED'] } })">{{ $t('f-synthesis:unlockedGps') | capitalize }}</a>
        <span>{{ unlockedCount }}</span>
      </li>

      <li class="list-group-item d-flex justify-content-between align-items-center border-0">
        <a id="goToConnectionAlarmsLink" :href="'/#/alarms/gateways?alarmType=CONNECTION'">{{ $t('f-synthesis:disconnectedAlarms') | capitalize }}</a>
        <span>{{ disconnectedAlarmsCount }}</span>
      </li>

      <li class="list-group-item d-flex justify-content-between align-items-center border-0">
        <a id="goToDoorAlarmsLink" :href="'/#/alarms/gateways?alarmType=DOOR'">{{ $t('f-synthesis:openDoorsAlarms') | capitalize }}</a>
        <span>{{ openAlarmsCount }}</span>
      </li>

      <li class="list-group-item d-flex justify-content-between align-items-center border-0">
        <a id="goToGpsAlarmsLink" :href="'/#/alarms/gateways?alarmType=GPS'">{{ $t('f-synthesis:unlockedGpsAlarms') | capitalize }}</a>
        <span>{{ unlockedAlarmsCount }}</span>
      </li>
    </ul>

    <div v-else slot="card-table" class="p-3 text-center font-italic text-muted">{{ $t('f-synthesis:empty') | capitalize }}</div>
  </card>
</template>

<script>
import { isEmpty, result, find } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'

export default {
  props: {
    // Update time, used to trigger and update from parent
    update: {
      type: Number,
      required: true
    },
    // Total of gateways to calculate %
    totalCount: {
      type: Number,
      required: true
    },
    // Last event counters for alarms
    lastEventsCounters: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      disconnectedCount: 0,
      openCount: 0,
      unlockedCount: 0,
      disconnectedAlarmsCount: 0,
      openAlarmsCount: 0,
      unlockedAlarmsCount: 0
    }
  },

  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(['selectedFleet'])
  },

  watch: {
    // When selectedFleet change
    update: 'getFleetSynthesis'
  },

  methods: {
    getFleetSynthesis () {
      if (this.totalCount > 0) {
        this.getDisconnectedGateways()
        this.getOpenGateways()
        this.getUnlockedGateways()
        this.getAlarmGateways()
      }
    },

    getFleetGatewaysStats (searchQuery = '') {
      let search = []

      // Filter by selected fleet
      if (!isEmpty(this.selectedFleet)) {
        search = this.$utils.addSearch(search, 'eq', this.selectedFleet.id, 'fleet.id')
      }

      if (searchQuery) {
        search = this.$utils.addSearch(search, searchQuery.operation, searchQuery.values, searchQuery.operand)
      }

      if (search.length > 0) {
        search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))
      }

      const params = { page: 1, pageSize: 1, fields: 'eui', search }
      return this.$api.getGateways(this.$store.getters.application, params)
    },

    async getDisconnectedGateways () {
      let error, response
      [error, response] = await to(this.getFleetGatewaysStats({ operation: 'eq', values: ['false'], operand: 'connection' }))
      if (error || !response) return

      this.disconnectedCount = response.data.totalCount
    },

    async getOpenGateways () {
      let error, response
      [error, response] = await to(this.getFleetGatewaysStats({ operation: 'eq', values: ['OPEN'], operand: 'door' }))
      if (error || !response) return

      this.openCount = response.data.totalCount
    },

    async getUnlockedGateways () {
      let error, response
      [error, response] = await to(this.getFleetGatewaysStats({ operation: 'eq', values: ['UNLOCKED'], operand: 'gps' }))
      if (error || !response) return

      this.unlockedCount = response.data.totalCount
    },

    getAlarmGateways () {
      this.disconnectedAlarmsCount = result(find(this.lastEventsCounters.counters, { type: 'ALARM', name: 'CONNECTION' }) || { count: 0 }, 'count')

      this.openAlarmsCount = result(find(this.lastEventsCounters.counters, { type: 'ALARM', name: 'DOOR' }) || { count: 0 }, 'count')

      this.unlockedAlarmsCount = result(find(this.lastEventsCounters.counters, { type: 'ALARM', name: 'GPS' }) || { count: 0 }, 'count')
    },

    setGatewayFilters (filters) {
      this.$store.commit('setGatewaysFilters', filters)
    }
  }
}
</script>

<style scoped lang="scss">
  #synthesis {
    min-height: 310px;
  }
</style>
